import { Show } from "@refinedev/antd";
import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Descriptions, Empty, List, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useParams } from "react-router";

import { api } from "../api";
import { DATE_TIME_FORMAT, ERROR_PROJECTS, ERROR_STATUS } from "../labels";
import type { SystemErrorDetail } from "../types";

type Status = SystemErrorDetail["status"];

const ACTIONS: { status: Status; label: string; done: string }[] = [
  {
    status: "resolved",
    label: "Çözüldü olarak işaretle",
    done: "Hata çözüldü olarak işaretlendi.",
  },
  { status: "ignored", label: "Yok say", done: "Hata yok sayıldı." },
  { status: "unresolved", label: "Tekrar aç", done: "Hata tekrar açıldı." },
];

/**
 * /system-errors/:id (permission `system.errors`): one error, its last occurrence with the
 * stack trace (newest frame first), and its status. A change of status is written to the audit
 * log. The full history stays in GlitchTip.
 */
export function SystemErrorShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const {
    data: item,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["office", "system-error", id],
    queryFn: () => api<SystemErrorDetail>(`/api/office/system-errors/${encodeURIComponent(id)}`),
  });

  async function setStatus(action: (typeof ACTIONS)[number]) {
    setBusy(true);
    try {
      await api(`/api/office/system-errors/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: { status: action.status },
      });
      open?.({ type: "success", message: action.done });
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: ["office", "system-errors"] }),
      ]);
    } catch (failure) {
      open?.({
        type: "error",
        message: failure instanceof Error ? failure.message : "Hata oluştu.",
      });
    } finally {
      setBusy(false);
    }
  }

  const event = item?.lastEvent ?? null;

  return (
    <Show
      title="Sistem hatası"
      isLoading={isLoading}
      canEdit={false}
      canDelete={false}
      headerButtons={
        item ? (
          <Space wrap>
            {ACTIONS.filter((action) => action.status !== item.status).map((action) => (
              <Button key={action.status} loading={busy} onClick={() => void setStatus(action)}>
                {action.label}
              </Button>
            ))}
            <Button href={item.url} target="_blank" rel="noreferrer">
              GlitchTip'te aç
            </Button>
          </Space>
        ) : null
      }
    >
      {error ? <Alert type="error" message={error.message} showIcon /> : null}
      {item ? (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Hata">
              <Typography.Text strong>{item.title}</Typography.Text>
            </Descriptions.Item>
            {item.culprit ? (
              <Descriptions.Item label="Yer">{item.culprit}</Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Uygulama">
              {ERROR_PROJECTS[item.project] ?? item.project}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={ERROR_STATUS[item.status]?.color}>{ERROR_STATUS[item.status]?.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Kaç kez">{item.count}</Descriptions.Item>
            <Descriptions.Item label="İlk görülme">
              {dayjs(item.firstSeen).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Son görülme">
              {dayjs(item.lastSeen).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Kod">{item.shortId}</Descriptions.Item>
            {event?.tags.map((tag) => (
              <Descriptions.Item key={tag.key} label={tag.key}>
                {tag.value}
              </Descriptions.Item>
            ))}
          </Descriptions>

          <Card title="Son olay" size="small">
            {event?.exception ? (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Typography.Text strong>
                  {event.exception.type}: {event.exception.value}
                </Typography.Text>
                <List
                  size="small"
                  dataSource={event.exception.frames}
                  locale={{ emptyText: "Yığın izi yok." }}
                  renderItem={(frame, index) => (
                    <List.Item key={index}>
                      <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
                        <Typography.Text
                          type={frame.inApp ? undefined : "secondary"}
                          style={{ wordBreak: "break-all" }}
                        >
                          <Typography.Text code>{frame.function ?? "?"}</Typography.Text>{" "}
                          {frame.file ?? ""}
                          {frame.line !== null ? `:${frame.line}` : ""}
                          {frame.column !== null ? `:${frame.column}` : ""}
                          {frame.inApp ? null : " (kütüphane)"}
                        </Typography.Text>
                        {frame.context ? (
                          <Typography.Text type="secondary" code style={{ wordBreak: "break-all" }}>
                            {frame.context}
                          </Typography.Text>
                        ) : null}
                      </Space>
                    </List.Item>
                  )}
                />
              </Space>
            ) : event?.message ? (
              <Typography.Text>{event.message}</Typography.Text>
            ) : (
              <Empty description="Olay kaydı yok." />
            )}
          </Card>
        </Space>
      ) : null}
    </Show>
  );
}
